import React from "react";
import InscriptionCardInfo from "./InscriptionCardInfo";
import InscriptionCardContext from "./InscriptionCardContext";
import InscriptionCardDecision from "./InscriptionCardDecision";
import InscriptionCardDirector from "./InscriptionCardDirector";
import { getSanctionTypeStyle, isOver62 } from "./inscriptionHelpers";
import { colors, gradients, transition } from "../theme";

const EpSessionTableV2 = ({
  inscriptions,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onOpenDecision,
  isClosed,
  isDirector,
  isReadOnly,
  directorDecisions,
  onDirectorAccord,
  onDirectorSurseoir,
  isSavingDirector,
}) => {
  if (inscriptions.length === 0) {
    return (
      <div
        className="text-center p-5 rounded"
        style={{ background: colors.bg.page, border: "1px dashed #cbd5e1" }}
      >
        <div
          className="d-inline-flex align-items-center justify-content-center mb-3"
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: colors.border.default,
          }}
        >
          <i
            className="fa fa-users"
            style={{ fontSize: 24, color: colors.text.muted }}
          />
        </div>
        <p
          className="mb-0"
          style={{ fontSize: 15, color: colors.text.secondary }}
        >
          Aucun allocataire inscrit à cette session.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex flex-column" style={{ gap: 12 }}>
        {inscriptions.map((inscription, index) => {
          const isSelected = selectedIds.includes(inscription.id);
          const sanctionStyle = getSanctionTypeStyle(inscription.type);
          const over62 = isOver62(inscription);
          const hasDecision = !!inscription.decisionId;

          return (
            <div
              key={inscription.id}
              id={`inscription-${inscription.id}`}
              className="rounded animated fadeIn faster"
              style={{
                background: colors.bg.white,
                border: isSelected
                  ? `2px solid ${colors.primary.main}`
                  : over62
                    ? "2px solid #f59e0b"
                    : `1px solid ${colors.border.default}`,
                boxShadow: isSelected
                  ? "0 0 0 3px rgba(37,99,235,0.1)"
                  : "0 1px 3px rgba(0,0,0,0.04)",
                overflow: "hidden",
                transition,
              }}
            >
              {/* Top accent bar */}
              <div
                style={{
                  height: 3,
                  background: hasDecision
                    ? gradients.progressComplete
                    : `linear-gradient(90deg, ${sanctionStyle.border}, ${sanctionStyle.border}88)`,
                }}
              />

              <div className="d-flex">
                {/* Checkbox column */}
                {(!isClosed || isDirector) && !isReadOnly && (
                  <div
                    className="d-flex align-items-start justify-content-center pt-3"
                    style={{ width: 48, flexShrink: 0 }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleSelect(inscription.id)}
                      style={{
                        width: 18,
                        height: 18,
                        cursor: "pointer",
                        accentColor: colors.primary.main,
                      }}
                    />
                  </div>
                )}

                {/* Main content */}
                <div className="flex-grow-1 p-3" style={{ minWidth: 0 }}>
                  <div className="row">
                    {/* Col 1: Allocataire info */}
                    <div
                      className={`col-12 ${isDirector ? "col-lg-2" : "col-lg-3"} mb-2 mb-lg-0`}
                    >
                      <InscriptionCardInfo
                        inscription={inscription}
                        index={index}
                        hasDecision={hasDecision}
                        isClosed={isClosed}
                      />
                    </div>

                    {/* Col 2: Type + Motif */}
                    <div
                      className={`col-12 ${isDirector ? "col-lg-4" : "col-lg-3"} mb-2 mb-lg-0`}
                    >
                      <InscriptionCardContext inscription={inscription} />
                    </div>

                    {/* Col 3: Avis EP */}
                    <div
                      className={`col-12 ${isDirector ? "col-lg-4" : "col-lg-4"} mb-2 mb-lg-0`}
                    >
                      <InscriptionCardDecision
                        inscription={inscription}
                        hasDecision={hasDecision}
                      />
                    </div>

                    {/* Col directeur : accord / surseoir */}
                    {isDirector && (
                      <div className="col-12 col-lg-2 mb-2 mb-lg-0 d-flex align-items-center">
                        <InscriptionCardDirector
                          inscription={inscription}
                          directorDecision={directorDecisions?.[inscription.id]}
                          isReadOnly={isReadOnly}
                          isSaving={isSavingDirector === inscription.id}
                          onAccord={onDirectorAccord}
                          onSurseoir={onDirectorSurseoir}
                          onOpenDecision={onOpenDecision}
                        />
                      </div>
                    )}

                    {/* Col 4: Action */}
                    {!isDirector && (
                      <div
                        className="col-12 col-lg-2 d-flex flex-column align-items-end"
                        style={{ gap: 6 }}
                      >
                        {inscription.note && (
                          <div
                            style={{
                              fontSize: 11,
                              color: colors.info.text,
                              background: colors.info.bg,
                              border: `1px solid ${colors.info.border}`,
                              borderRadius: 6,
                              padding: "4px 8px",
                              lineHeight: 1.4,
                              width: "100%",
                            }}
                          >
                            <i className="fa fa-sticky-note mr-1" />
                            {inscription.note}
                          </div>
                        )}
                        <button
                          className="btn btn-sm"
                          onClick={() => onOpenDecision(inscription)}
                          style={{
                            background: hasDecision
                              ? colors.bg.page
                              : gradients.primary,
                            color: hasDecision
                              ? colors.text.secondary
                              : colors.text.white,
                            border: hasDecision
                              ? `1px solid ${colors.border.default}`
                              : "none",
                            borderRadius: 8,
                            padding: "8px 16px",
                            fontSize: 13,
                            fontWeight: 600,
                            boxShadow: hasDecision
                              ? "none"
                              : "0 2px 6px rgba(37,99,235,0.25)",
                            transition,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {hasDecision ? (
                            <>
                              <i
                                className={`fa fa-${isClosed ? "eye" : "pencil"} mr-1`}
                                style={{ fontSize: 11 }}
                              />
                              {isClosed ? "Consulter" : "Modifier"}
                            </>
                          ) : (
                            <>
                              <i
                                className="fa fa-gavel mr-1"
                                style={{ fontSize: 11 }}
                              />
                              Décider
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Over 62 warning banner */}
                  {over62 && (
                    <div
                      className="d-flex align-items-center mt-2 px-3 py-2 rounded"
                      style={{
                        background: `linear-gradient(135deg, ${colors.warning.light}, ${colors.warning.border})`,
                        gap: 8,
                      }}
                    >
                      <i
                        className="fa fa-exclamation-triangle"
                        style={{ fontSize: 13, color: colors.warning.text }}
                      />
                      <span
                        style={{
                          fontSize: 12,
                          color: colors.warning.text,
                          fontWeight: 600,
                        }}
                      >
                        ATTENTION : Allocataire de plus de 62 ans — Ne doit pas
                        être sanctionné
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(EpSessionTableV2);
